import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Mail, Pencil, Plus } from "lucide-react";
import { api, FORMAT_LABELS } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog } from "../../components/ui/overlay";
import { Skeleton } from "../../components/ui/primitives";
import { cn } from "../../lib/utils";
import { presetLabel } from "../../lib/time";
import { describeCadence } from "./cadence";
import { LastRun, ScheduleStatus } from "./ScheduleParts";
import { ScheduleForm } from "./ScheduleForm";

// Pinned near the top rather than centred, so it doesn't jump as lists load and sections open.
const DIALOG = "top-[6vh] translate-y-0 w-[600px] max-w-[calc(100vw-32px)] max-h-[88vh] overflow-y-auto p-6";

/**
 * "Schedule email" in the dashboard menu: your schedules for this dashboard, and a new one starting
 * from the locations the dashboard is showing (`locationIds`, or all of the session's).
 */
export function DashboardSchedulesDialog({
  open, onOpenChange, dashboardId, title, locationIds, staffSession,
}: { open: boolean; onOpenChange: (o: boolean) => void; dashboardId: string; title: string; locationIds?: string[]; staffSession: boolean }) {
  const schedules = useQuery({ queryKey: ["schedules"], queryFn: api.schedules, enabled: open });
  const session = useQuery({ queryKey: ["locations"], queryFn: api.locations, enabled: open });
  const mine = schedules.data?.filter((s) => s.dashboardId === dashboardId) ?? [];
  const [view, setView] = useState<"list" | "new" | { edit: string }>("list");
  // Each time it opens: straight to a new schedule when there isn't one yet.
  useEffect(() => {
    if (open && schedules.isSuccess) setView(mine.length ? "list" : "new");
    // Only on opening (and once the list first arrives), not on every refetch.
  }, [open, schedules.isSuccess]);

  const initialLocations = (session.data ?? [])
    .filter((l) => !locationIds || locationIds.includes(String(l.id)))
    .map((l) => ({ id: String(l.id), name: String(l.name) }));
  const back = () => (mine.length ? setView("list") : onOpenChange(false));

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={`Schedule ${title}`} className={DIALOG}>
      <h2 className="flex items-center gap-2 text-base font-semibold tracking-tight"><Mail className="size-4 text-subtle" /> Email “{title}” on a schedule</h2>
      <p className="mt-1 mb-5 text-sm text-muted">
        Each send runs the dashboard as it is then, as you, and emails the file as an attachment.
      </p>
      {schedules.isLoading || session.isLoading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9" />)}</div>
      ) : schedules.error ? (
        <p className="text-sm text-bad">{schedules.error.message}</p>
      ) : view === "new" ? (
        <ScheduleForm
          dashboardId={dashboardId} initialLocations={initialLocations} staffSession={staffSession}
          onDone={() => setView("list")} onCancel={back}
        />
      ) : typeof view === "object" ? (
        <EditSchedule id={view.edit} staffSession={staffSession} onDone={() => setView("list")} />
      ) : (
        <>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {mine.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{describeCadence(s.cadence)}</span>
                  <span className="block truncate text-xs text-subtle">
                    {FORMAT_LABELS[s.format]} · {presetLabel(s.timePreset)} · {s.locationIds.length} location{s.locationIds.length === 1 ? "" : "s"} · {s.people.map((p) => p.name).join(", ")}
                  </span>
                </span>
                <ScheduleStatus s={s} />
                <LastRun run={s.lastRun} />
                <Button size="icon-sm" variant="ghost" aria-label="Edit schedule" onClick={() => setView({ edit: s.id })}><Pencil className="size-3.5" /></Button>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex items-center justify-between">
            <Link to="/schedules" className="text-xs text-muted hover:text-foreground hover:underline" onClick={() => onOpenChange(false)}>All my schedules</Link>
            <Button variant="primary" onClick={() => setView("new")}><Plus className="size-3.5" /> New schedule</Button>
          </div>
        </>
      )}
    </Dialog>
  );
}

/** Edit one of your schedules from My schedules, or (`duplicate`) start a new one from its settings. */
export function EditScheduleDialog({ id, duplicate, open, onOpenChange, staffSession }: {
  id: string; duplicate?: boolean; open: boolean; onOpenChange: (o: boolean) => void; staffSession: boolean;
}) {
  const title = duplicate ? "Duplicate schedule" : "Edit schedule";
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} className={DIALOG}>
      <h2 className={cn("flex items-center gap-2 text-base font-semibold tracking-tight", !duplicate && "mb-5")}><Mail className="size-4 text-subtle" /> {title}</h2>
      {duplicate && (
        <p className="mt-1 mb-5 text-sm text-muted">A new schedule with the same settings. Change what differs, such as the locations and who gets it.</p>
      )}
      <EditSchedule id={id} duplicate={duplicate} staffSession={staffSession} onDone={() => onOpenChange(false)} />
    </Dialog>
  );
}

function EditSchedule({ id, duplicate, staffSession, onDone }: { id: string; duplicate?: boolean; staffSession: boolean; onDone: () => void }) {
  const detail = useQuery({ queryKey: ["schedule", id], queryFn: () => api.schedule(id) });
  if (detail.isLoading) return <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9" />)}</div>;
  if (detail.error) return <p className="text-sm text-bad">{detail.error.message}</p>;
  const s = detail.data!;
  return <ScheduleForm key={s.id} dashboardId={s.dashboardId} schedule={s} duplicate={duplicate} staffSession={staffSession} onDone={onDone} onCancel={onDone} />;
}
